<template>
  <div class="project-slider" :style="{ width: width ? `${width}px` : '100%' }" @touchstart="onTouchStart"
    @touchmove="onTouchMove" @touchend="onTouchEnd">

    <!-- Desktop fade mode -->
    <template v-if="!isMobile">
      <transition name="project-slider-fade" mode="out-in">
        <div v-if="currentItem && currentItem.type" :key="currentIndex" class="project-slider__fade-container">

          <div class="project-slider__slide">
            <!-- Image Item -->
            <div v-if="currentItem.type === $constants.PROJECT_TYPE_IMAGE" class="project-slider__slide-image"
              :class="{ 'project-slider__slide-image--no-caption': !currentItem.image_caption }">
              <div class="project-slider__image-wrapper">
                <img :src="`https://guillaumevieira.com/assets/${currentItem.image_src}`"
                  :alt="imageAltText(currentItem)" class="project-slider__image" draggable="false" />
              </div>
              <div v-if="currentItem.image_caption" class="project-slider__caption project-slider__caption--desktop">
                <div class="project-slider__caption-text" v-html="parseHtml(currentItem.image_caption)" />
              </div>
            </div>

            <!-- Video Item -->
            <div v-else-if="currentItem.type === $constants.PROJECT_TYPE_VIDEO" class="project-slider__slide-video"
              :class="{ 'project-slider__slide-video--no-caption': !currentItem.image_caption }">
              <div class="project-slider__video-wrapper">
                <video controls playsinline preload="metadata" class="project-slider__video">
                  <source :src="`https://guillaumevieira.com/assets/${currentItem.image_src}`" type="video/mp4" />
                </video>
              </div>
              <div v-if="currentItem.image_caption" class="project-slider__caption project-slider__caption--desktop">
                <div class="project-slider__caption-text" v-html="parseHtml(currentItem.image_caption)" />
              </div>
            </div>

            <!-- Text Item -->
            <div v-else-if="currentItem.type === $constants.PROJECT_TYPE_TEXT" class="project-slider__slide-text">
              <TextFade :current-text="parseText(currentItem)" />
            </div>
          </div>

        </div>
      </transition>
    </template>

    <!-- Mobile slider mode -->
    <template v-else>
      <!-- Custom horizontal slider container -->
      <div ref="sliderContainer" class="project-slider__container" :style="{ height: height ? `${height}px` : '100%' }"
        @scroll="onScroll">

        <div v-for="(item, index) in projectItems" :key="index" class="project-slider__slide slide" :data-index="index">

          <!-- Image Item -->
          <div v-if="item.type === $constants.PROJECT_TYPE_IMAGE" class="project-slider__slide-image"
            :class="{ 'project-slider__slide-image--has-scrollable-caption': captionNeedsScroll(item) }">
            <div class="project-slider__image-wrapper">
              <img :src="`https://guillaumevieira.com/assets/${item.image_src}`" :alt="imageAltText(item)"
                class="project-slider__image" draggable="false" />
            </div>
            <div v-if="item.image_caption" :ref="`caption-${index}`" class="project-slider__caption"
              :class="{ 'project-slider__caption--collapsed': captionNeedsScroll(item) }">
              <div class="project-slider__caption-text" v-html="parseHtml(item.image_caption)" />
            </div>
          </div>

          <!-- Video Item -->
          <div v-else-if="item.type === $constants.PROJECT_TYPE_VIDEO" class="project-slider__slide-video"
            :class="{ 'project-slider__slide-video--no-caption': !item.image_caption }">
            <div class="project-slider__video-wrapper">
              <video controls playsinline preload="metadata" class="project-slider__video">
                <source :src="`https://guillaumevieira.com/assets/${item.image_src}`" type="video/mp4" />
              </video>
            </div>
            <div v-if="item.image_caption" :ref="`caption-${index}`" class="project-slider__caption"
              :class="{ 'project-slider__caption--collapsed': captionNeedsScroll(item) }">
              <div class="project-slider__caption-text" v-html="parseHtml(item.image_caption)" />
            </div>
          </div>

          <!-- Text Item -->
          <div v-else-if="item.type === $constants.PROJECT_TYPE_TEXT" class="project-slider__slide-text">
            <TextFade :current-text="parseText(item)" />
          </div>

        </div>
      </div>
    </template>

  </div>
</template>

<script>
import TextFade from '@/components/utils/TextFade.vue';

export default {
  name: 'ProjectSlider',
  components: {
    TextFade,
  },

  props: {
    items: Array,
    projectName: String,
    width: Number,
    height: Number,
    onUpdate: Function,
  },

  computed: {
    isMobile() {
      // Match CSS mobile detection: either width or height ≤ 600px
      return this.windowWidth <= 600 || this.windowHeight <= 600; // $mobile-screen-width
    },
    currentItem() {
      return this.projectItems[this.currentIndex] || {};
    }
  },

  data() {
    return {
      projectItems: [],
      currentIndex: 0,
      initialIndex: null,
      isScrolling: false,
      scrollTimeout: null,
      observer: null,

      // Window tracking for responsive behavior
      windowWidth: 0,
      windowHeight: 0,

      // Touch gesture tracking
      touchStartX: 0,
      touchStartY: 0,
      isHorizontalSwipe: false,
      isZooming: false,

      // Zoom reset
      initialScale: 1,
    };
  },

  created() {
    this.initialIndex = parseInt(this.$route.query.p) || 0;
  },

  mounted() {
    this.projectItems = this.items || [];
    this.windowWidth = window.innerWidth; // Initialize window width
    this.windowHeight = window.innerHeight; // Initialize window height
    this.setupScrollDetection();
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    window.removeEventListener('resize', this.handleResize);
  },

  watch: {
    items(val) {
      this.projectItems = val || [];
      this.$nextTick(() => {
        this.setupScrollDetection();

        const index = parseInt(this.$route.query.p) || 0;
        if (index > 0 && index < this.projectItems.length) {
          this.goToSlide(index, false);
        } else if (index >= 0 && index < this.projectItems.length) {
          this.currentIndex = index;
          this.onSlideChange(index);
        } else {
          this.currentIndex = 0;
          this.onSlideChange(0);
        }
      });
    },

    // Handle URL changes (back/forward navigation)
    '$route.query.p'(newIndex) {
      if (newIndex !== undefined && newIndex !== null) {
        this.goToSlide(parseInt(newIndex), true);
      }
    }
  },

  methods: {
    setupScrollDetection() {
      // Only setup scroll detection on mobile
      if (this.isMobile) {
        const container = this.$refs.sliderContainer;
        if (!container) return;

        // Clean up existing observer
        if (this.observer) {
          this.observer.disconnect();
        }

        // Use IntersectionObserver to detect visible slide
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !this.isScrolling) {
                const index = parseInt(entry.target.dataset.index);
                if (!isNaN(index) && index !== this.currentIndex) {
                  this.currentIndex = index;
                  this.onSlideChange(index);
                }
              }
            });
          },
          {
            root: container,
            threshold: 0.6 // 60% visible = active slide
          }
        );

        const slides = container.querySelectorAll('.slide');
        slides.forEach(slide => this.observer.observe(slide));
      }
    },

    onScroll() {
      // Debounce scroll events to avoid excessive updates
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
      this.isScrolling = true;
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
      }, 150);
    },

    handleResize() {
      // Update window dimensions for responsive behavior
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;

      // Re-setup IntersectionObserver when viewport resizes
      // This is needed because the observer's root bounds become stale on resize
      this.$nextTick(() => {
        this.setupScrollDetection();
      });
    },

    onSlideChange(index) {
      // Update URL without triggering route change
      window.history.replaceState(null, '', `?p=${index}`);

      // Notify parent component
      if (this.onUpdate) {
        this.onUpdate(index, this.projectItems[index], this.projectItems);
      }
    },

    goToSlide(index, smooth = true) {
      // On desktop, just update the index for fade transition
      if (!this.isMobile) {
        if (index >= 0 && index < this.projectItems.length) {
          this.currentIndex = index;
          this.onSlideChange(index);
        }
        return;
      }

      // On mobile, use scroll behavior
      const container = this.$refs.sliderContainer;
      if (!container) return;

      const slides = container.querySelectorAll('.slide');
      const targetSlide = slides[index];

      if (targetSlide) {
        container.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: smooth ? 'smooth' : 'auto'
        });
        this.currentIndex = index;
        this.onSlideChange(index);
      }
    },

    // Touch gesture handlers
    onTouchStart(e) {
      // Detect pinch zoom start
      if (e.touches.length === 2) {
        this.isZooming = true;
        if (window.visualViewport) {
          this.initialScale = window.visualViewport.scale;
        }
        return;
      }

      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.isHorizontalSwipe = false;
    },

    onTouchMove(e) {
      // Handle pinch zoom - prevent zoom on slider area
      if (e.touches.length === 2) {
        e.preventDefault();
        return;
      }

      // Detect swipe direction for horizontal vs vertical scroll handling
      const deltaX = Math.abs(e.touches[0].clientX - this.touchStartX);
      const deltaY = Math.abs(e.touches[0].clientY - this.touchStartY);

      if (deltaX > deltaY && deltaX > 30) {
        this.isHorizontalSwipe = true;
      }
    },

    onTouchEnd(e) {
      // Reset zoom when pinch ends
      if (this.isZooming && e.touches.length < 2) {
        this.isZooming = false;
        this.resetZoom();
      }

      this.isHorizontalSwipe = false;
    },

    resetZoom() {
      // Instagram-like zoom reset
      if (window.visualViewport) {
        try {
          window.visualViewport.scale = 1;
        } catch (e) {
          // Fallback if direct scale setting fails
        }
      }

      // Scroll to origin to reset any pan offset
      window.scrollTo(0, 0);

      // Reset meta viewport temporarily
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        const original = viewport.getAttribute('content');
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        setTimeout(() => {
          viewport.setAttribute('content', original);
        }, 100);
      }
    },

    // Caption detection
    captionNeedsScroll(item) {
      // Use isMobile computed property for consistent mobile detection
      if (!this.isMobile) return false;
      if (!item || item.type !== this.$constants.PROJECT_TYPE_IMAGE) return false;
      if (!item.image_caption) return false;

      const plainText = item.image_caption.replace(/<[^>]*>/g, '');
      return plainText.length > 80;
    },

    parseHtml(content) {
      if (!content) return null;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(
        content.replaceAll('&lt;', '<').replaceAll('&gt;', '>'),
        'text/html'
      );
      return xmlDoc.body.innerHTML;
    },

    parseText(item) {
      if (item && item.text) {
        return this.parseHtml(item.text);
      }
      return null;
    },

    imageAltText(item) {
      let text = '';
      if (item.image_caption) {
        const raw = item.image_caption.replaceAll('&lt;', '<').replaceAll('&gt;', '>');
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(raw, 'text/html');
        text = xmlDoc.body.textContent || '';
        if (text.length > 100) text = text.substring(0, 100) + '...';
      }
      return text || `Photo #${this.currentIndex + 1}`;
    },

    // Public methods for navigation arrows
    prev() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    },

    next() {
      if (this.currentIndex < this.projectItems.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.project-slider {
  width: 100%;
  height: 100%;
  position: relative;

  // Allow both horizontal and vertical gestures
  touch-action: pan-x pan-y;

  // Custom slider container with CSS scroll snap
  &__container {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE/Edge

    // Hide scrollbar
    &::-webkit-scrollbar {
      display: none;
    }

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      overflow-x: scroll;
      overflow-y: hidden;
      // Allow slides to extend beyond viewport for vertical scroll
      align-items: flex-start;
    }
  }

  // Individual slide
  &__slide {
    flex: 0 0 100%;
    width: 100%;
    scroll-snap-align: start;
    scroll-snap-stop: always;
    box-sizing: border-box;
    background-color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      height: 100%;
      overflow: hidden;
      align-items: flex-start;
      margin-right: 20px;
    }
  }

  // Image slide
  &__slide-image {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;

    // Desktop: proper flex layout with caption at bottom
    @media screen and (min-width: #{$mobile-screen-width + 1}),
    screen and (min-height: #{$mobile-screen-width + 1}) {
      overflow: hidden; // Prevent vertical scrolling on desktop
      justify-content: flex-start;
      align-items: stretch;
      max-height: calc(100vh - 130px); // Constrain to viewport minus navbar
    }

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      overflow: hidden;
      justify-content: flex-start;
      align-items: stretch;
      max-height: calc(100vh - 80px);
    }

    @media screen and (orientation: landscape) and (max-height: $mobile-screen-width) {
      max-height: 100vh;
    }

    // No caption on desktop - image takes full space with bottom margin
    &--no-caption {

      @media screen and (min-width: #{$mobile-screen-width + 1}),
      screen and (min-height: #{$mobile-screen-width + 1}) {
        .project-slider__image-wrapper {
          padding-bottom: 60px;
        }
      }
    }
  }

  &__image-wrapper {
    flex: 1 1 auto;
    display: flex;
    justify-content: center;
    max-width: 100%;
    overflow: hidden;
    width: 100%;
    min-height: 0; // Allow flex item to shrink properly

    // Desktop: stretch image to fill wrapper height, object-fit handles aspect ratio
    @media screen and (min-width: #{$mobile-screen-width + 1}),
    screen and (min-height: #{$mobile-screen-width + 1}) {
      align-items: stretch;
      padding: 0 40px 0 0;
    }

    // Mobile: stretch image to fill remaining space after caption
    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      align-items: stretch;
      flex: 1;
      padding-right: 0;
      padding-bottom: 10px;
    }
  }

  &__image {
    object-fit: contain;
    display: block;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;

    // Desktop: stretch on wrapper fills height, object-fit preserves aspect ratio
    @media screen and (min-width: #{$mobile-screen-width + 1}),
    screen and (min-height: #{$mobile-screen-width + 1}) {
      width: 100%;
    }

    // Mobile: fill wrapper, object-fit preserves aspect ratio
    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      width: 100%;
    }
  }

  // Caption
  &__caption {
    flex: 0 0 auto;
    padding: 10px;
    padding-left: 0;
    text-align: right;

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      padding: 10px;
      text-align: left;
      // Ensure caption is visible and not hidden
      position: static;
      width: 100%;
      box-sizing: border-box;
      // Don't constrain height - let it show all content
      // Remove overflow to let parent slide handle scrolling
      overflow: visible;
    }

    @media screen and (orientation: landscape) and (max-height: $mobile-screen-width) {
      display: none;
    }
  }

  // Desktop caption with reserved space
  &__caption--desktop {

    @media screen and (min-width: #{$mobile-screen-width + 1}),
    screen and (min-height: #{$mobile-screen-width + 1}) {
      flex: 0 0 160px;
      width: auto;
      // max-width: 80%;
      // // max-width: 400px;
      // margin-right: 40px;
      // margin-right: $content-padding-left;
      padding: 20px;
      overflow-y: auto;
      overflow-x: hidden;
      background-color: white;
      text-align: left;
      box-sizing: border-box;
      align-self: flex-end;

      // Custom scrollbar
      scrollbar-width: thin;
      scrollbar-color: #ccc #f0f0f0;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: #f0f0f0;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #ccc;
        border-radius: 3px;
      }
    }

    @media screen and (min-width: #{$mobile-screen-width + 1}) {
      max-width: 100%;
      margin-right: 40px;
    }

    // @media screen and (min-width: 860px) {
    //   max-width: 100%;
    //   margin-right: 40px;
    // }

    @media screen and (min-width: 1000px) {
      max-width: 50%;
      margin-right: 100px;
    }
  }

  &__caption-text {
    font-size: 13px;
    line-height: 17px;
    color: #414141;

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      font-size: 13px;
    }

    ::v-deep * {
      margin: 0;
      padding: 0;
    }
  }

  // Video and text slides
  &__slide-text {
    overflow: hidden;
    padding-right: $content-padding-left;

    @media screen and (min-width: #{$mobile-screen-width + 1}),
    screen and (min-height: #{$mobile-screen-width + 1}) {
      height: calc(100vh - 130px);
      width: 100%;
      align-self: flex-start;
      overflow-y: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding-right: 0;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    @media (orientation: landscape) and (max-height: $mobile-screen-width) {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }

  &__slide-video {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;

    @media screen and (min-width: #{$mobile-screen-width + 1}),
    screen and (min-height: #{$mobile-screen-width + 1}) {
      overflow: hidden;
      justify-content: flex-start;
      align-items: stretch;
      max-height: calc(100vh - 130px);
    }

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      height: 100%;
      overflow: hidden;
      justify-content: flex-start;
      align-items: stretch;
      max-height: calc(100vh - 80px);
    }

    &--no-caption {

      @media screen and (min-width: #{$mobile-screen-width + 1}),
      screen and (min-height: #{$mobile-screen-width + 1}) {}
    }
  }

  &__video-wrapper {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    overflow: hidden;
    width: 100%;
    min-height: 0;

    @media screen and (min-width: #{$mobile-screen-width + 1}),
    screen and (min-height: #{$mobile-screen-width + 1}) {
      padding: 0 40px 0 0;
    }

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      align-items: center;
      flex: 1;
      padding-right: 0;
      padding-bottom: 10px;
    }
  }

  &__video {
    max-width: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    background-color: #000;

    @media screen and (min-width: #{$mobile-screen-width + 1}),
    screen and (min-height: #{$mobile-screen-width + 1}) {
      max-height: calc(100vh - 130px - 160px);

      .project-slider__slide-video--no-caption & {
        max-height: calc(100vh - 130px);
      }
    }

    @media screen and (max-width: $mobile-screen-width),
    screen and (max-height: $mobile-screen-width) {
      width: 100%;
      max-height: 100%;
    }

    @media screen and (orientation: landscape) and (max-height: $mobile-screen-width) {
      max-height: 100vh;
    }
  }

  // Desktop fade mode
  &__fade-container {
    display: flex;
    align-items: flex-start; // Changed from center to allow caption at bottom
    justify-content: center;
    width: 100%;
    background-color: white;
    min-height: 100%; // Use min-height instead of fixed height
    overflow: visible; // Allow caption to be visible
  }
}

// Fade transition for desktop
.project-slider-fade-enter-active,
.project-slider-fade-leave-active {
  transition: opacity 0.4s ease;
}

.project-slider-fade-enter {
  opacity: 0;
}

.project-slider-fade-leave-to {
  opacity: 0;
}

.project-slider-fade-enter-to,
.project-slider-fade-leave {
  opacity: 1;
}
</style>
